const BRAND_PATH = '/aha';

/**
 * Interface for API endpoint configuration
 */
interface ApiEndpoint {
  path: string;
  requiresAuth?: boolean;
}

/**
 * API endpoints configuration
 */
export const API_ENDPOINTS: Record<string, ApiEndpoint> = {
  GENERATE_DEVICE_ACTIVATION_CODE: {
    path: `${BRAND_PATH}/generateDeviceActivationCode`,
  },
  GET_ENTITLEMENTS: {
    path: `${BRAND_PATH}/getEntitlements`,
    requiresAuth: true,
  },
  AUTHENTICATE_CUSTOMER_FOR_PARTNER: {
    path: `${BRAND_PATH}/authenticateCustomerForPartner`,
  },
  REFRESH_TOKEN: { path: `${BRAND_PATH}/refreshToken` },
  GET_ACCOUNT_PROFILE: {
    path: `${BRAND_PATH}/getAccountProfile`,
    requiresAuth: true,
  },
  GET_CONTACT: { path: `${BRAND_PATH}/getContact`, requiresAuth: true },
  UPDATE_PROFILE: { path: `${BRAND_PATH}/updateProfile`, requiresAuth: true },
  GET_VOD_ENTITLEMENTS: {
    path: `${BRAND_PATH}/getVODEntitlements`,
    requiresAuth: true,
  },
  GET_VOD_CATALOG: { path: `${BRAND_PATH}/getVODCatalog` },
  LOGOUT_USER: { path: `${BRAND_PATH}/logOutUser`, requiresAuth: true },
  LOGOUT_DEVICE: {
    path: `${BRAND_PATH}/removeDeviceForAccount`,
    requiresAuth: true,
  },
  ADD_SUBSCRIPTION: {
    path: `${BRAND_PATH}/addSubscription`,
    requiresAuth: true,
  },
  GET_PRODUCTS: { path: `${BRAND_PATH}/getProducts` },
  CREATE_PAYMENT_QR_CODE: {
    path: `${BRAND_PATH}/createPaymentQRCode`,
    requiresAuth: true,
  },
  GET_TRANSACTION_STATUS: {
    path: `${BRAND_PATH}/getTransactionStatusV2`,
    requiresAuth: true,
  },
  GET_TRANSACTION_STATUS_V3: {
    path: `${BRAND_PATH}/getTransactionStatusV3`,
    requiresAuth: true,
  },
  SEND_PAYTM_QR_PAYMENT_LINK: {
    path: `${BRAND_PATH}/sendPayTMQRPaymentLink`,
    requiresAuth: true,
  },
  CREATE_JIO_PAY_ORDER: {
    path: `${BRAND_PATH}/createJioPayOrder`,
    requiresAuth: true,
  },
  RECORD_PLAY_TIME: {
    path: `${BRAND_PATH}/recordPlayTime`,
    requiresAuth: true,
  },
  GET_FAVORITE_LIST: { path: '/user/favorite/list', requiresAuth: true },
  GET_BOOKMARK_LIST: { path: '/user/bookmark/list', requiresAuth: true },
  GET_CONTENT: { path: '/content' },
  GET_GEO_LOCATION: { path: '/device/location/lookup' },
  VALIDATE_PARENTAL_CONTROL_PIN: {
    path: `${BRAND_PATH}/validateParentalControlPIN`,
    requiresAuth: true,
  },
};

/**
 * Gets the complete URL for an API endpoint
 * @param baseUrl - The base URL of the API
 * @param endpointKey - The key of the endpoint in API_ENDPOINTS
 * @returns string - The complete URL
 * @throws Error if the endpoint does not exist
 */
export const getApiUrl = (baseUrl: string, endpointKey: string): string => {
  const endpoint = API_ENDPOINTS[endpointKey];
  if (!endpoint) {
    throw new Error(`API endpoint '${endpointKey}' not found`);
  }
  return `${baseUrl}${endpoint.path}`;
};

/**
 * Checks if an endpoint requires authentication
 * @param endpointKey - The key of the endpoint in API_ENDPOINTS
 * @returns boolean - True if the endpoint requires authentication
 * @throws Error if the endpoint does not exist
 */
export const requiresAuth = (endpointKey: string): boolean => {
  const endpoint = API_ENDPOINTS[endpointKey];
  if (!endpoint) {
    throw new Error(`API endpoint '${endpointKey}' not found`);
  }
  return endpoint.requiresAuth || false;
};

// Legacy export for backward compatibility
export const API_CONSTANTS: Record<string, string> = Object.keys(
  API_ENDPOINTS
).reduce((acc, key) => {
  acc[key] = API_ENDPOINTS[key].path;
  return acc;
}, {} as Record<string, string>);
